import { useUserStore } from "~/stores/user";
import { useRuntimeConfig, useFetch } from '#imports';

/**
 * Utilises useFetch for SSR -> composable must be used in setup
 *
 * @param {GraphQL} query
 * @param {keyPair} variables
 * @param {callback} transform
 * @param fetchOnServer
 * @returns
 */
async function fetchGraphQL (query, variables = {}, transform = null, fetchOnServer = true) {
  return new Promise(async (resolve, reject) => {
    const config = useRuntimeConfig()
    const userStore = useUserStore()
    const wooToken = userStore.getWooSessionToken;
    let headers;

    if (wooToken) {
      headers = {
        'Content-Type': 'application/json',
        'woocommerce-session': `Session ${wooToken}`
      };
    }

    await useFetch(config.public.wordpressUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        query,
        variables
      }),
      server: true,
      immediate: true,
      onResponse (context) {
        userStore.setWooSessionToken(context.response.headers.get('Woocommerce-Session'));
      },
    }).then((response) => {
      if (response?.error?.value) {
        reject(response.error.value);
      } else if (transform) {
        resolve(transform(response));
      } else {
        resolve(response?.data?.value);
      }
    }).catch((e) => {
      reject(e);
    });
  });
}

/**
 * Utilises $fetch for Client Only data (cart, viewer)
 *
 * @param {GraphQL} query
 * @param {keyPair} variables
 * @param {callback} transform
 * @param fetchOnServer
 * @returns
 */
async function fetchClientGraphQL (query, variables = {}, transform = null, fetchOnServer = true) {
  return new Promise(async (resolve, reject) => {
    const config = useRuntimeConfig()
    const userStore = useUserStore()
    const wooToken = userStore.getWooSessionToken;
    let headers;

    if (wooToken) {
      headers = {
        'Content-Type': 'application/json',
        'woocommerce-session': `Session ${wooToken}`
      };
    }

    $fetch(config.public.wordpressUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        query,
        variables
      }),
      server: fetchOnServer,
      onResponse (context) {
        userStore.setWooSessionToken(context.response.headers.get('Woocommerce-Session'));
      },
    }).then((response) => {
      if (response.errors) {
        reject(response.errors);
      } else if (transform) {
        resolve(transform(response));
      } else {
        resolve(response.data);
      }
    }).catch((e) => {
      reject(e);
    });
  });
}

function mutateGraphQL (mutation) {
  const handleMutation = (variables) => {
    return new Promise((resolve, reject) => {
      const config = useRuntimeConfig();
      const userStore = useUserStore();
      const wooToken = userStore.getWooSessionToken;
      let headers;

      if (wooToken) {
        headers = {
          'Content-Type': 'application/json',
          'woocommerce-session': `Session ${wooToken}`
        };
      }

      $fetch(config.public.wordpressUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          query: mutation,
          variables
        }),
        server: false,
        onResponse (context) {
          userStore.setWooSessionToken(context.response.headers.get('Woocommerce-Session'));
        },
      }).then((response) => {
        if (response.errors) {
          reject(response);
        } else {
          resolve({ data: response.data });
        }
      }).catch((e) => {
        reject(e);
      });
    });
  }

  return {
    mutationHandler: handleMutation,
  }
}

export {
  fetchGraphQL,
  fetchClientGraphQL,
  mutateGraphQL,
};
